/**
 * React hook for the current org's Alfie-plan capabilities (sidebar gating).
 *
 * Kept separate from `alfie-plan.ts` so the route-guard module stays React-free
 * (importing a context module into a route file creates an import cycle that
 * collapses route types). Both share the same query cache key.
 */
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { ALFIE_CAP_STALE_MS, alfieCapabilitiesKey, fetchAlfieCapabilities } from '@/api/alfie-plan';

export interface AlfieCapabilities {
  /** True until the org + capabilities have resolved — don't gate while loading. */
  isLoading: boolean;
  /** The capability read errored — treat as UNKNOWN (no padlock, no upsell). */
  isError: boolean;
  /** Resolved and the org has no paid Alfie plan (drives the upgrade upsell). */
  isFree: boolean;
  hasAlfieChat: boolean;
  hasCommunity: boolean;
  has: (feature: string) => boolean;
}

export function useAlfieCapabilities(): AlfieCapabilities {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const { data, isLoading, isError } = useQuery({
    queryKey: orgId ? alfieCapabilitiesKey(orgId) : ['alfie-capabilities', 'none'],
    queryFn: () => fetchAlfieCapabilities(orgId as string),
    enabled: !!orgId,
    staleTime: ALFIE_CAP_STALE_MS,
  });
  const caps = data ?? [];
  const loading = isLoading || !orgId;
  const hasAlfieChat = caps.includes('alfie_chat');
  const hasCommunity = caps.includes('community');
  return {
    isLoading: loading,
    isError,
    // Any paid Alfie plan grants at least alfie_chat + community; their absence
    // means Free — but ONLY on a settled, successful read. While loading or on a
    // read error the state is unknown, so the upsell never flashes prematurely
    // and a blip never mislabels a paying user as Free.
    isFree: !loading && !isError && !hasAlfieChat && !hasCommunity,
    hasAlfieChat,
    hasCommunity,
    has: (f: string) => caps.includes(f),
  };
}
